/**
 * BCP-47 tag comparison, reduced to what this API actually emits and no further.
 *
 * The corpus is small and irregular: audio adaptation sets use `ar`, `de`, `zh-Hans`, `zh-Hant`;
 * caption tracks add `de-DE`, `pt-BR`, `es-419`. So "German" can arrive as `de` from the manifest
 * and `de-DE` from the caption list for the same video, and `zh-Hans` must not be considered the
 * same track as `zh-Hant`.
 *
 * Two levels, and the caller is expected to try them in order: an **exact** match on the
 * normalised tag, then a **primary subtag** match. Never a fuzzy one — silently handing someone
 * Traditional Chinese because Simplified was missing is exactly the failure §7 forbids for audio.
 *
 * R1: no `Intl`, no `Locale`. Plain string work.
 */

/** Lowercased, `_` folded to `-`, empty segments dropped. `''` for anything unusable. */
export function normalizeLangTag(tag: string): string {
  return tag
    .trim()
    .replace(/_/g, '-')
    .toLowerCase()
    .split('-')
    .filter((part) => part !== '')
    .join('-');
}

/** `zh-hans` → `zh`. `''` stays `''`. */
export function primaryLangSubtag(tag: string): string {
  const normalized = normalizeLangTag(tag);
  const dash = normalized.indexOf('-');
  return dash === -1 ? normalized : normalized.slice(0, dash);
}

export function langTagsEqual(a: string, b: string): boolean {
  const left = normalizeLangTag(a);
  return left !== '' && left === normalizeLangTag(b);
}

/** True when the two tags share a primary subtag — `de` vs `de-DE`, `zh-Hans` vs `zh-Hant`. */
export function langTagsRelated(a: string, b: string): boolean {
  const left = primaryLangSubtag(a);
  return left !== '' && left === primaryLangSubtag(b);
}
